import { useState, useRef, useCallback, useLayoutEffect, useMemo, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import { createCascadeAssigner } from '@overlayqa/shared';
import type { ColumnDef, RowAction, BulkAction } from './DataTableTypes';
import { RowActionMenu } from './RowActionMenu';
import { BulkActionBar } from './BulkActionBar';
import { firstSelectedIds } from '@overlayqa/shared';
import { FloatingBulkActionBar } from './FloatingBulkActionBar';
import { Checkbox } from '../ui/Checkbox';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowRight01Icon } from '@hugeicons/core-free-icons';

/**
 * Descendants that own their own clicks. Without this the first-column <Link>
 * would fire the row handler too and one click would navigate twice.
 *
 * `[role="option"]` and `[role="listbox"]` are here for the same reason
 * `[role="menuitem"]` is, and were missing until 2026-09-18: a floating menu
 * renders through a PORTAL, so in the DOM its items are children of <body>, but
 * React propagates their clicks along the React tree, which runs through this
 * row. A menu's items were named and a listbox's were not, so picking a value
 * from an editable cell changed it AND opened the issue. Found by
 * e2e/verify-inline-cell-edit.ts, whose "saved" screenshot was the detail page;
 * pinned by __tests__/DataTableOptionClick.test.tsx.
 */
const INTERACTIVE_SELECTOR =
  'a, button, input, select, textarea, label, [role="button"], [role="link"], [role="menuitem"], [role="option"], [role="listbox"], [role="checkbox"], [contenteditable="true"]';

/**
 * Whether a click on a row means "open this row".
 *
 * No keyboard equivalent is added here, deliberately. The row click is a
 * REDUNDANT pointer affordance: the destination is already reachable from the
 * keyboard via the first column's link, so WCAG 2.1.1 is satisfied by that
 * link. Giving the <tr> role="button" + tabIndex would add a second tab stop
 * per row and make keyboard navigation of a long issues list worse, not
 * better. This is the Linear/GitHub arrangement.
 *
 * Every refusal below is a bug that turning the row click on would otherwise
 * introduce, which is why they ship with it rather than after it:
 *
 *  - an interactive descendant already handled it (double navigation);
 *  - the click finished a text selection INSIDE this row, so navigating would
 *    throw the selection away. Session replay of the recorded dead clicks could
 *    not rule out text-selection as the intent, so this is not hypothetical;
 *  - a modifier is held, which means "new tab/window". A <tr> has no href to
 *    honour that with, so the row stands aside and lets the first-column link
 *    be the thing that does.
 *
 * The selection check is scoped to the clicked row on purpose. Suppressing on
 * ANY non-empty selection would mean "select an issue title to copy it, then
 * click a different row" does nothing — a new dead click, i.e. the exact class
 * this change exists to remove. A selection we cannot locate (no anchorNode)
 * still suppresses, because losing a selection is worse than costing a click.
 */
function isRowOpenClick(event: MouseEvent<HTMLTableRowElement>): boolean {
  if (event.defaultPrevented) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;

  const target = event.target as Element | null;
  if (target?.closest?.(INTERACTIVE_SELECTOR)) return false;

  const selection = typeof window !== 'undefined' ? window.getSelection?.() : null;
  if (selection && selection.toString().length > 0) {
    const anchor = selection.anchorNode;
    if (!anchor || event.currentTarget.contains(anchor)) return false;
  }

  return true;
}

interface DataTableProps<T> {
  /**
   * Ids that just arrived from a bulk create, so they land one after another
   * instead of in one frame. Absent on every table that has no such moment,
   * which leaves this entirely inert for them.
   */
  cascadeArrivals?: readonly string[];
  columns: ColumnDef<T>[];
  data: T[];
  rowKey: (item: T) => string;
  rowActions?: RowAction<T>[];
  bulkActions?: BulkAction<T>[];
  bulkActionPlacement?: 'inline' | 'bottom';
  emptyState: ReactNode;
  onRowClick?: (item: T) => void;
  /** Used for aria-label on the action menu. Receives item, returns label text. */
  rowLabel?: (item: T) => string;
  loading?: boolean;
  /**
   * Renders the row-selection checkbox column. Defaults to true so every
   * existing consumer is unchanged. Set to false for tables with no
   * bulkActions, where the checkboxes would otherwise select rows that
   * nothing acts on (a dead control).
   */
  selectable?: boolean;
  /** Extra class for one row (an ignored issue renders at opacity-45). Absent = unchanged. */
  rowClassName?: (item: T) => string | undefined;
  /**
   * Most rows a bulk action may carry (the issue bulk routes cap a request at
   * 100). Over it, the bar blocks actions and offers "Select first N".
   * Absent = no cap.
   */
  selectionLimit?: number;
}

export function DataTable<T>({
  columns,
  data,
  rowKey,
  cascadeArrivals,
  rowClassName,
  selectionLimit,
  rowActions,
  bulkActions,
  bulkActionPlacement = 'inline',
  emptyState,
  onRowClick,
  rowLabel = () => 'item',
  loading = false,
  selectable = true,
}: DataTableProps<T>) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [hasHiddenColumns, setHasHiddenColumns] = useState(false);

  const allIds = data.map(rowKey);
  const visibleSelectedIds = new Set(allIds.filter((id) => selectedIds.has(id)));

  // IssuesPage passes its filtered rows as data (pages/IssuesPage.tsx:463-465).
  // Keep every bulk-action consumer on that same visible selection, including
  // getSubmenu and isDisabled during render, before any effect could run.
  // Prune the stored selection too: restoring a filter must not reselect rows
  // the user already hid. Updating this component's own state conditionally
  // makes React retry its render before committing children with stale state.
  if (visibleSelectedIds.size !== selectedIds.size) {
    setSelectedIds(visibleSelectedIds);
  }

  // One pass, one assigner: slots are paid out in RENDER order, which is the
  // order the reader sees the rows land. A row that did not just arrive spends
  // no slot, so a pre-existing row between two arrivals cannot open a gap.
  const cascadeSlots = useMemo(() => {
    const slotFor = createCascadeAssigner(cascadeArrivals ?? []);
    const slots = new Map<string, number>();
    for (const item of data) {
      const id = rowKey(item);
      const slot = slotFor(id);
      if (slot !== null) slots.set(id, slot);
    }
    return slots;
  }, [data, cascadeArrivals, rowKey]);
  const selectedCount = visibleSelectedIds.size;
  const allSelected = allIds.length > 0 && allIds.every((id) => visibleSelectedIds.has(id));
  const someSelected = selectedCount > 0 && !allSelected;

  // Sync indeterminate state
  if (headerCheckboxRef.current) {
    headerCheckboxRef.current.indeterminate = someSelected;
  }

  const toggleAll = useCallback(() => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allIds));
    }
  }, [allSelected, allIds]);

  const toggleRow = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  /**
   * `overflow-x-auto` (ce5f02e7, 2026-08-17) made a too-wide table REACHABLE
   * by scrolling but not DISCOVERABLE: at rest, with no gesture, nothing on
   * screen said more columns were off to the side, and overlay-scrollbar
   * platforms (macOS/iOS) paint no scrollbar until a scroll gesture starts.
   * Measured live at 390px on the Schedule table: only the URL column
   * rendered and Pause/Run now were unreachable without discovering
   * horizontal scroll by accident.
   *
   * This is the "always-visible scroll affordance" named as one of three fix
   * candidates for that finding (over a sticky actions column or a stacked
   * mobile layout) because it needs no restructuring of the table this file
   * already renders, so it carries none of their layout risk.
   *
   * Re-measured on window resize (viewport narrowing is the reported
   * trigger) and whenever the table's own shape changes, since adding a
   * column or an actions cell can push it past its container without the
   * window moving.
   *
   * `useLayoutEffect`, not `useEffect`: this is a DOM measurement (the
   * Tooltip component next door uses the same hook for the same reason), and
   * `useEffect` runs after the browser has already painted, so the first
   * frame of an overflowing table would render with no hint at all.
   */
  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const measure = () => {
      // 1px of slack absorbs the subpixel rounding some browsers report for
      // scrollWidth, which would otherwise flicker the hint at the exact
      // width where the table just barely fits.
      setHasHiddenColumns(el.scrollWidth - el.clientWidth > 1);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [data.length, columns.length, rowActions?.length, selectable]);

  // Wrap bulk actions to clear selection on success
  const wrappedBulkActions = bulkActions?.map((action) => ({
    ...action,
    onClick: async (ids: string[]) => {
      try {
        await action.onClick(ids);
        if (action.clearSelectionOnSuccess !== false) clearSelection();
      } catch {
        // The action's mutation owns failure feedback. Retain the selection
        // for retry, and settle the event handler on both toolbar placements.
      }
    },
  }));

  if (data.length === 0) {
    return <>{emptyState}</>;
  }

  const overSelectionLimit = selectionLimit !== undefined && visibleSelectedIds.size > selectionLimit;
  const selectFirstAllowed = () => setSelectedIds(new Set(firstSelectedIds(allIds, visibleSelectedIds, selectionLimit)));
  const ActionBar = bulkActionPlacement === 'bottom' ? FloatingBulkActionBar : BulkActionBar;
  const actionBar = selectedCount > 0 && wrappedBulkActions && wrappedBulkActions.length > 0 ? (
    <ActionBar selectedCount={selectedCount} actions={wrappedBulkActions} selectedIds={Array.from(visibleSelectedIds)} loading={loading} overSelectionLimit={overSelectionLimit} onSelectFirstAllowed={selectFirstAllowed} />
  ) : null;

  return (
    <div>
      {/* Bulk Action Bar */}
      {bulkActionPlacement === 'inline' && actionBar}

      {/* Table */}
      {/* overflow-x-auto, NOT overflow-hidden. The hidden form was here to clip
          the rounded-lg corners, and it also clipped the TABLE: once the
          intrinsic width exceeded this container the overflow was amputated
          rather than scrollable, so `scrollWidth === clientWidth` and there was
          no scrollbar to reach the lost columns. Measured 2026-08-17 in real
          Chromium on the Schedule table: the row-action trigger was
          un-hit-testable at 1120, 1024, 820, 480 and 390px and never recovered,
          because this wrapper shrinks faster than the responsive column-hiding
          reduces the table. At 820px "Not scanned yet" rendered one letter per
          line with Status and Actions gone entirely.
          `overflow-x-auto` still establishes the clipping context the corners
          need on the horizontal axis and adds a scrollbar only when one is
          earned. Pinned by __tests__/dataTableHorizontalScroll.source.test.ts. */}
      {/* tabIndex + aria-label only while it is actually scrollable (WCAG
          2.1.1 / technique SCR29): an overflow-x-auto div is not focusable by
          default, so a keyboard-only user could not reach it at all, and an
          unconditional tab stop on a table that already fits would be a dead
          one — the same "tint, no pointer, no handler" shape this file's own
          row-click history already names for a different control. */}
      <div ref={scrollContainerRef} tabIndex={hasHiddenColumns ? 0 : undefined} aria-label={hasHiddenColumns ? 'Scrollable table, more columns to the right' : undefined} className={`bg-white rounded-lg border border-[var(--color-card-stroke)] overflow-x-auto transition-opacity ${loading ? 'opacity-60' : ''}`}>
        <table className="w-full">
          <thead className="bg-gray-50/50 border-b border-gray-100">
            <tr>
              {/* Checkbox column */}
              {selectable && (
                <th className="w-8 p-0 align-middle">
                  {/* Same hit-area fix as the row cells below. One of the two
                      recorded header dead clicks (2026-08-06 12:04) is a
                      `th...w-8` miss: the user aimed at select-all and landed
                      in the cell's padding, which was inert. */}
                  <label className="flex items-center pl-3 pr-0 py-3 cursor-pointer">
                    <Checkbox
                      ref={headerCheckboxRef}
                      checked={allSelected}
                      onChange={toggleAll}
                      aria-label="Select all"
                      aria-checked={someSelected ? 'mixed' : allSelected}
                    />
                  </label>
                </th>
              )}

              {/* Data columns */}
              {columns.map((col) => (
                <th
                  key={col.header}
                  className={`px-4 py-3 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider ${col.className || ''}`}
                >
                  {col.header}
                </th>
              ))}

              {/* Actions column */}
              {rowActions && rowActions.length > 0 && (
                <th className="w-12 px-3 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {data.map((item, index) => {
              const id = rowKey(item);
              const isSelected = visibleSelectedIds.has(id);
              const zebra = index % 2 === 1 ? 'bg-gray-50/60' : '';

              return (
                <tr
                  key={id}
                  /* `group/row` lets a CELL reveal something while the pointer is
                     anywhere on this row: the issues table's editable badges show
                     their hint arrow that way, so a list at rest looks untouched.
                     NAMED, because the issue title's Link is a plain `group` nested
                     inside this row and an unnamed group here would lose to it.
                     Inert on every row with no group-aware descendant.
                     Pinned by __tests__/DataTableRowGroup.test.tsx. */
                  /* The hover tint is gated on onRowClick, not unconditional.
                     Ungated it lit a row under the cursor and then refused the
                     click, because no product page passed onRowClick at all —
                     measured as 9 dead clicks from 4 external users over 11
                     days, every one on /projects/:id/issues. `cursor-pointer`
                     was already gated, so the shipped combination was "tint,
                     no pointer, no handler". */
                  className={`group/row transition-colors ${isSelected ? 'bg-primary-50/30' : zebra} ${onRowClick ? `${isSelected ? 'hover:bg-primary-50/50' : 'hover:bg-gray-100/60'} cursor-pointer` : ''} ${cascadeSlots.has(id) ? 'dt-row--entering' : ''} ${rowClassName?.(item) ?? ''}`}
                  style={cascadeSlots.has(id) ? ({ '--i': String(cascadeSlots.get(id)) } as CSSProperties) : undefined}
                  onClick={onRowClick ? (e) => { if (isRowOpenClick(e)) onRowClick(item); } : undefined}
                >
                  {/* Checkbox cell */}
                  {selectable && (
                    <td className="w-8 p-0 align-middle" onClick={(e) => e.stopPropagation()}>
                      {/* The label carries the cell's padding so the whole cell
                          toggles. A bare 16px box left the surrounding 32px cell
                          as dead space. */}
                      <label className="flex items-center pl-3 pr-0 py-3 cursor-pointer">
                        <Checkbox
                          checked={isSelected}
                          onChange={() => toggleRow(id)}
                          aria-label={`Select ${rowLabel(item)}`}
                        />
                      </label>
                    </td>
                  )}

                  {/* Data cells */}
                  {columns.map((col) => (
                    <td key={col.header} className={`px-4 py-3 align-middle ${col.className || ''}`}>
                      {col.render(item)}
                    </td>
                  ))}

                  {/* Action menu cell */}
                  {rowActions && rowActions.length > 0 && (
                    <td className="w-12 px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <RowActionMenu
                        item={item}
                        actions={rowActions}
                        label={`Actions for ${rowLabel(item)}`}
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Always visible whenever the table overflows, never gated on a
          gesture — the whole point is telling the user before they scroll,
          not after. */}
      {hasHiddenColumns && (
        <p
          data-testid="table-scroll-hint"
          className="mt-2 flex items-center gap-1 text-[10px] text-gray-600"
        >
          Scroll right to see more
          <HugeiconsIcon
            icon={ArrowRight01Icon}
            size={12}
            strokeWidth={2}
            color="currentColor"
            className="w-3 h-3"
            aria-hidden="true"
          />
        </p>
      )}
      {bulkActionPlacement === 'bottom' && actionBar}
    </div>
  );
}
